import * as R from 'ramda';
import * as Yup from 'yup';
import { withFormik } from 'formik';
import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  pure,
  compose,
  lifecycle,
  withState,
  withProps,
  withHandlers,
} from 'react-recompose';
// components
import { TabsMui } from '../../../../components/tabs-mui';
import { FormFooter } from '../../../../components/form-footer';
// features
import { makeSelectMasterSettings } from '../../../branch/selectors';
// forms
import { Fieldset2 } from '../../../../forms';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
import { ENUMS } from '../../../../constants/enums';
import { EXPENSE_PARTY_OPTIONS, EXPENSE_CHARGE_TYPE_OPTIONS } from '../../../../helpers/options';
// hooks
import { useAiRecognize } from '../../../../hooks';
// icons
import { AmousLogoAiComponent } from '../../../../svgs/amous-animated-logo/animated-logo';
// ui
import { Box, Flex, scrollableContainerCss4px } from '../../../../ui';
// utilities
import { sendRequest } from '../../../../utilities/http';
import endpointsMap from '../../../../utilities/endpoints';
// fleet-profile
import {
  makeSelectDriverExpenseTypes,
  makeSelectAvailableAssignable,
} from '../../selectors';
import {
  getDriverExpenseTypesSuccess,
  getAvailableAssignableSuccess,
} from '../../actions';
import ExpenseAiUpload from './expense-ai-upload';
import ExpenseDocuments from './expense-documents';
import ExpenseDocumentsUpdate from './expense-documents-update';
//////////////////////////////////////////////////

const getValidationSchema = (values, types) => {
  const { typeGuid } = values;

  let schema = {
    files: Yup.array().notRequired(),
    [GC.FIELD_DATE]: G.yupStringRequired,
    [GC.FIELD_CURRENCY]: G.yupStringRequired,
    [GC.FIELD_EXPENSE_PARTY]: G.yupStringRequired,
    [GC.FIELD_EXPENSE_TYPE_GUID]: G.yupStringRequired,
    [GC.FIELD_EXPENSE_CHARGE_TYPE]: G.yupStringRequired,
    [GC.FIELD_LOCATION]: Yup.object()
      .nullable(true)
      .typeError(G.getSelectLocationLocaleTxt()),
    [GC.FIELD_DESCRIPTION]: Yup.string()
      .nullable(true)
      .max(5000, G.getShouldBeLessOrEqualLocaleTxt(2000)),
    [GC.FIELD_TOTAL]: Yup.number()
      .nullable(true)
      .required(G.getRequiredLocaleTxt())
      .positive(G.getShouldBePositiveLocaleTxt())
      .max(99999, G.getShouldBeLessOrEqualLocaleTxt(99999)),
  };

  if (R.pathEq(GC.CONFIGURATION_MAINTENANCE_TYPE_TRUCK, [typeGuid, GC.FIELD_CONFIGURATION_MAINTENANCE_TYPE], types)) {
    schema = R.assoc(GC.FIELD_TRUCK_GUID, G.yupStringRequired, schema);
  }

  if (R.pathEq(GC.CONFIGURATION_MAINTENANCE_TYPE_TRAILER, [typeGuid, GC.FIELD_CONFIGURATION_MAINTENANCE_TYPE], types)) {
    schema = R.assoc(GC.FIELD_TRAILER_GUID, G.yupStringRequired, schema);
  }

  return schema;
};

const inputWrapperStyles = { mb: 25, width: 200 };

const getFieldDisplay = (props) => {
  const { type, values, indexedExpenseTypes } = props;

  const { typeGuid } = values;

  const condition = R.or(G.isNilOrEmpty(typeGuid), R.and(
    G.isNotNilAndNotEmpty(typeGuid),
    R.not(R.pathEq(
      type,
      [typeGuid, GC.FIELD_CONFIGURATION_MAINTENANCE_TYPE],
      indexedExpenseTypes,
    )),
  ));

  if (condition) return 'none';
};

const getFields = (values, indexedExpenseTypes) => [
  {
    isRequired: true,
    inputWrapperStyles,
    type: 'reactSelect',
    options: 'expenseTypes',
    shouldCustomChange: true,
    label: ['titles:expense-type'],
    fieldName: GC.FIELD_EXPENSE_TYPE_GUID,
  },
  {
    isRequired: true,
    isClearable: true,
    type: 'datePicker',
    inputWrapperStyles,
    timeSelection: true,
    label: ['titles:date'],
    fieldName: GC.FIELD_DATE,
    calendarInputWrapperStyles: { width: '100%' },
  },
  {
    isRequired: true,
    inputWrapperStyles,
    type: 'reactSelect',
    options: EXPENSE_CHARGE_TYPE_OPTIONS,
    fieldName: GC.FIELD_EXPENSE_CHARGE_TYPE,
    label: ['titles:charge-type', 'Charge Type'],
  },
  {
    isRequired: true,
    inputWrapperStyles,
    type: 'reactSelect',
    options: EXPENSE_PARTY_OPTIONS,
    fieldName: GC.FIELD_EXPENSE_PARTY,
    label: ['titles:expense-party', 'Expense Party'],
  },
  {
    type: 'text',
    isRequired: true,
    inputWrapperStyles,
    label: ['titles:total'],
    fieldName: GC.FIELD_TOTAL,
  },
  {
    isRequired: true,
    inputWrapperStyles,
    type: 'reactSelect',
    fieldName: GC.FIELD_CURRENCY,
    options: R.tail(GC.CURRENCY_OPTIONS),
    label: ['titles:currency', 'Currency'],
  },
  {
    inputWrapperStyles,
    useFormattedAddress: true,
    label: ['titles:location'],
    type: 'addressAutocomplete',
    fieldName: GC.FIELD_LOCATION,
    locationMapper: (location) =>
      R.mergeRight(location, {
        [GC.FIELD_LOCATION_NAME]: '',
        [GC.FIELD_ADDRESS]: G.getPropFromObject(GC.FIELD_ADDRESS_1, location),
      }),
  },
  {
    type: 'text',
    inputWrapperStyles,
    fieldName: GC.GRC.LOCATION_NAME,
    label: ['titles:location-name', 'Location Name'],
  },
  {
    isRequired: true,
    options: 'trucks',
    type: 'reactSelect',
    label: ['titles:truck'],
    fieldName: GC.FIELD_TRUCK_GUID,
    inputWrapperStyles: {
      ...inputWrapperStyles,
      display: getFieldDisplay({
        values,
        indexedExpenseTypes,
        type: GC.CONFIGURATION_MAINTENANCE_TYPE_TRUCK,
      }),
    },
  },
  {
    isRequired: true,
    type: 'reactSelect',
    options: 'trailers',
    label: ['titles:trailer'],
    fieldName: GC.FIELD_TRAILER_GUID,
    inputWrapperStyles: {
      ...inputWrapperStyles,
      display: getFieldDisplay({
        values,
        indexedExpenseTypes,
        type: GC.CONFIGURATION_MAINTENANCE_TYPE_TRAILER,
      }),
    },
  },
  {
    type: 'textarea',
    label: ['titles:description'],
    fieldName: GC.FIELD_DESCRIPTION,
    inputWrapperStyles: { mb: 25, width: 415 },
  },
];

const withAvailableTruckAndTrailers = compose(
  withState(
    'trucks',
    'setTrucks',
    ({ availableAssignable }) => R.pathOr(null, ['availableTrucks'], availableAssignable),
  ),
  withState(
    'trailers',
    'setTrailers',
    ({ availableAssignable }) => R.pathOr(null, ['availableTrailers'], availableAssignable),
  ),
  withState(
    'expenseTypes',
    'setExpenseTypes',
    R.pathOr([], ['expenseTypeList']),
  ),
  withProps(({ trucks, trailers, expenseTypes = [] }) => ({
    indexedExpenseTypes: R.indexBy(R.prop(GC.FIELD_GUID), expenseTypes),
    optionsForSelect: {
      trucks,
      trailers,
      expenseTypes: R.map(
        ({ name, guid }) => ({
          [GC.FIELD_VALUE]: guid,
          [GC.FIELD_LABEL]: name,
        }),
        expenseTypes,
      ),
    },
  })),
  withHandlers({
    getAvailableEntitiesRequest: (props) => async (groupName) => {
      const {
        setTrucks,
        openLoader,
        setTrailers,
        closeLoader,
        initialValues,
        getAvailableAssignableSuccess,
      } = props;

      G.callFunction(openLoader);

      const options = {
        params: {
          currentDriverGuid: initialValues.driverGuid,
          [GC.BRANCH_GUID]: initialValues.enterpriseGuid,
        },
      };

      const isTrucks = R.equals(groupName, 'availableTrucks');

      const endpoint = G.ifElse(isTrucks, 'getAvailableTrucks', 'getAvailableTrailers');

      const res = await sendRequest('get', endpointsMap[endpoint], options);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        const options = R.map(({ guid, unitId, lastName, firstName }) => ({
          [GC.FIELD_VALUE]: guid,
          [GC.FIELD_LABEL]: R.or(unitId, `${firstName} ${lastName}`),
        }), R.or(data, []));

        if (isTrucks) {
          setTrucks(options);
        } else {
          setTrailers(options);
        }

        getAvailableAssignableSuccess({ options, groupName });
      } else {
        G.handleException('error getAvailableEntitiesRequest');
      }

      G.callFunction(closeLoader);
    },
    getExpenseTypes: (props) => async () => {
      const {
        openLoader,
        closeLoader,
        initialValues,
        setExpenseTypes,
        getDriverExpenseTypesSuccess,
      } = props;

      G.callFunction(openLoader);

      const options = {
        params: {
          [GC.BRANCH_GUID]: initialValues.enterpriseGuid,
        },
      };

      const res = await sendRequest('get', endpointsMap.driverExpenseTypeList, options);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        setExpenseTypes(data);
        getDriverExpenseTypesSuccess(data);
      } else {
        G.handleException('error getExpenseTypes');
      }

      G.callFunction(closeLoader);
    },
  }),
  lifecycle({
    componentDidMount() {
      const {
        trucks,
        trailers,
        expenseTypeList,
        getExpenseTypes,
        getAvailableEntitiesRequest,
      } = this.props;

      if (R.isNil(trucks)) getAvailableEntitiesRequest('availableTrucks');

      if (R.isNil(trailers)) getAvailableEntitiesRequest('availableTrailers');

      if (R.isNil(expenseTypeList)) getExpenseTypes();
    },
  }),
  pure,
);

const mapStateToProps = (state) => createStructuredSelector({
  expenseTypeList: makeSelectDriverExpenseTypes(state),
  availableAssignable: makeSelectAvailableAssignable(state),
});

const makeFiles = (data) => {
  if (G.isNilOrEmpty(data)) return [];

  return R.compose(
    R.map(R.prop('file')),
    R.reject(({ file }) => G.isNilOrEmpty(file)),
  )(data);
};

const enhance = compose(
  withState('documents', 'setDocuments', null),
  withState('aiUploadDoc', 'setAiUploadDoc', null),
  withState('initialValues', 'setInitialValues', ({ initialValues }) => initialValues),
  connect(mapStateToProps, { getDriverExpenseTypesSuccess, getAvailableAssignableSuccess }),
  withAvailableTruckAndTrailers,
  withFormik({
    enableReinitialize: true,
    validationSchema: ({ indexedExpenseTypes }) => Yup.lazy((values) => (
      Yup.object().shape(getValidationSchema(values, indexedExpenseTypes))
    )),
    handleSubmit: (values, { props: { documents, submitAction } }) => {
      const location = G.getPropFromObject(GC.FIELD_LOCATION, values);

      let valuesToUse = R.compose(
        R.mergeLeft({
          [GC.FIELD_LOCATION]: G.isNotNilAndNotEmpty(location) ? R.omit([GC.FIELD_GUID], location) : null,
          [GC.FIELD_DATE]: G.convertInstanceToDefaultDateTimeFormat(G.getPropFromObject(GC.FIELD_DATE, values)),
        }),
        G.mapObjectEmptyStringFieldsToNull,
      )(values);

      if (G.isNotNilAndNotEmpty(documents)) {
        const files = makeFiles(documents);

        valuesToUse = { ...valuesToUse, files };
      }

      submitAction(valuesToUse);
    },
    mapPropsToValues: ({ initialValues, fleetVendorGuid }) => {
      const { location } = initialValues;

      const expenseDefaultFields = {
        [GC.FIELD_DATE]: null,
        [GC.FIELD_TOTAL]: null,
        [GC.FIELD_FILES]: null,
        [GC.FIELD_LOCATION]: null,
        [GC.FIELD_CURRENCY]: null,
        [GC.FIELD_TRUCK_GUID]: null,
        [GC.FIELD_DESCRIPTION]: null,
        [GC.FIELD_TRAILER_GUID]: null,
        [GC.FIELD_EXPENSE_TYPE_GUID]: null,
        [GC.FIELD_EXPENSE_CHARGE_TYPE]: null,
        [GC.FIELD_EXPENSE_PARTY]: G.ifElse(fleetVendorGuid, ENUMS.ENUM_VENDOR, ENUMS.ENUM_COMPANY),
        [GC.FIELD_CURRENCY]: R.or(
          G.getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_DEFAULT_CURRENCY),
          GC.DEFAULT_UI_CURRENCY,
        ),
      };

      const locationToUse = G.isNilOrEmpty(location)
        ? null
        : R.assoc(GC.FIELD_ADDRESS_1, R.prop(GC.FIELD_ADDRESS, location), location);

      return G.setInitialFormikValues(expenseDefaultFields, R.assoc(GC.FIELD_LOCATION, locationToUse, initialValues));
    },
  }),
  withHandlers({
    handleCustomChange: (props) => (value, name) => {
      const { values, setValues, indexedExpenseTypes } = props;

      const type = R.path([value, GC.FIELD_CONFIGURATION_MAINTENANCE_TYPE], indexedExpenseTypes);

      let valuesToSet = {
        ...values,
        [name]: value,
      };

      if (R.equals(type, GC.CONFIGURATION_MAINTENANCE_TYPE_TRUCK)) {
        valuesToSet = {
          ...valuesToSet,
          [GC.FIELD_TRAILER_GUID]: '',
        };
      } else if (R.equals(type, GC.CONFIGURATION_MAINTENANCE_TYPE_TRAILER)) {
        valuesToSet = {
          ...valuesToSet,
          [GC.FIELD_TRUCK_GUID]: '',
        };
      } else {
        valuesToSet = {
          ...valuesToSet,
          [GC.FIELD_TRUCK_GUID]: '',
          [GC.FIELD_TRAILER_GUID]: '',
        };
      }

      setValues(valuesToSet);
    },
    handleSelectLocation: ({ setFieldValue }) => (location) => (
      setFieldValue(GC.FIELD_LOCATION, location)
    ),
    handleChangeLocation: ({ values, setFieldValue }) => ({ value }) => {
      if (R.path([GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_LOCATION_NAME], values)) {
        setFieldValue(GC.GRC.LOCATION_NAME, '');
      }

      setTimeout(() => setFieldValue(GC.FIELD_LOCATION, value), 0);
    },
    handleRemovePrevFile: ({ setFieldValue }) => () => setFieldValue('deleteFile', true),
    handleGetInitialValues: (props) => async () => {
      const { guid, openLoader, closeLoader, setInitialValues } = props;

      G.callFunction(openLoader);

      const res = await sendRequest('get', endpointsMap.driverExpenseByGuid(guid));

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        setInitialValues(
          R.assoc(GC.FIELD_EXPENSE_TYPE_GUID, R.pathOr(null, [GC.FIELD_TYPE, GC.FIELD_GUID], data), data),
        );
      } else {
        G.handleFailResponseSimple(res, true, 'handleGetInitialValues fail');
      }

      G.callFunction(closeLoader);
    },
  }),
  lifecycle({
    componentDidMount() {
      const { guid, handleGetInitialValues } = this.props;

      if (G.isNotNilAndNotEmpty(guid)) handleGetInitialValues();
    },
  }),
  pure,
);

const AiLoading = () => (
  <Flex mt={100} width={445} alignItems='center' justifyContent='center'>
    <AmousLogoAiComponent />
  </Flex>
);

const LeftSection = (props) => {
  const {
    values,
    isReadOnlyMode,
    optionsForSelect,
    aiRecognizeLoading,
    handleCustomChange,
    indexedExpenseTypes,
    handleSelectLocation,
    handleChangeLocation,
    disableSelectDriverField,
  } = props;

  const mappedFields = R.map(R.assoc('disabled', isReadOnlyMode), getFields(values, indexedExpenseTypes));

  return (
    <Box
      p={15}
      width={445}
      height='100%'
      flexShrink={0}
      overflow='auto'
      borderRight='1px solid'
      css={scrollableContainerCss4px}
      borderColor={G.getTheme('colors.lightGrey')}
    >
      { G.isTrue(aiRecognizeLoading) && <AiLoading /> }
      { G.isFalse(aiRecognizeLoading) && (
        <Fieldset2
          {...optionsForSelect}
          {...G.getFormikProps(props)}
          fields={mappedFields}
          optionsForSelect={optionsForSelect}
          handleCustomChange={handleCustomChange}
          customSelectLocationFunction={handleSelectLocation}
          fieldsWrapperStyles={{ mt: 15, justifyContent: 'space-between' }}
          handlers={{
            customHandleChange: handleChangeLocation,
            disableSelectDriverField: () => disableSelectDriverField,
          }}
        />
      )}
    </Box>
  );
};

const getTabs1 = (activeTab) => [
  {
    value: 0,
    withCount: R.equals(activeTab, 0),
    text: G.getWindowLocale('titles:ai-upload', 'AI Upload'),
  },
  {
    value: 1,
    withCount: R.equals(activeTab, 1),
    text: G.getWindowLocale('titles:documents', 'Documents'),
  },
];

const getTabs2 = (activeTab) => [
  {
    value: 0,
    withCount: R.equals(activeTab, 0),
    text: G.getWindowLocale('titles:documents', 'Documents'),
  },
];

const RightTabs = (props) => {
  const {
    aiEnabled,
    rightActiveTab,
    isReadOnlyMode,
    setRightActiveTab,
  } = props;

  const tabs = R.and(G.isTrue(aiEnabled), R.not(isReadOnlyMode)) ? getTabs1(rightActiveTab) : getTabs2(rightActiveTab);

  return (
    <TabsMui
      tabs={tabs}
      activeTab={rightActiveTab}
      setActiveTab={setRightActiveTab}
      tabStyles={GC.COMMON_MUI_TAB_STYLES}
      tabsStyles={{ ...GC.COMMON_MUI_TABS_STYLES, m: '15px' }}
    />
  );
};

const activeTabContentMap1 = {
  0: ExpenseAiUpload,
  1: ExpenseDocuments,
};

const activeTabContentMap2 = {
  0: ExpenseDocuments,
};

const activeTabContentUpdateMap1 = {
  0: ExpenseAiUpload,
  1: ExpenseDocumentsUpdate,
};

const activeTabContentUpdateMap2 = {
  0: ExpenseDocumentsUpdate,
};

const RightSection = (props) => {
  const {
    rightActiveTab,
    setRightActiveTab,
  } = props;

  const masterSettings = useSelector(makeSelectMasterSettings());

  const aiEnabled = R.pathOr(false, [GC.FIELD_BRANCH_SETTINGS_AI_ENABLED], masterSettings);

  const contentMap = G.isTrue(aiEnabled) ? activeTabContentMap1 : activeTabContentMap2;

  const Component = contentMap[rightActiveTab];

  return (
    <Box
      flexGrow={1}
      height='100%'
      minWidth={620}
      flexShrink={0}
      overflow='auto'
      maxWidth='calc(100vw - 840px)'
      css={scrollableContainerCss4px}
    >
      <RightTabs
        aiEnabled={aiEnabled}
        rightActiveTab={rightActiveTab}
        setRightActiveTab={setRightActiveTab}
      />
      {
        G.isNilOrEmpty(Component) ? null : <Component {...props} />
      }
    </Box>
  );
};

const UpdateRightSection = (props) => {
  const {
    isReadOnlyMode,
    rightActiveTab,
    setRightActiveTab,
  } = props;

  const masterSettings = useSelector(makeSelectMasterSettings());

  const aiEnabled = R.pathOr(false, [GC.FIELD_BRANCH_SETTINGS_AI_ENABLED], masterSettings);

  const contentMap = R.and(G.isTrue(aiEnabled), R.not(isReadOnlyMode)) ?
    activeTabContentUpdateMap1 :
    activeTabContentUpdateMap2;

  const Component = contentMap[rightActiveTab];

  return (
    <Box
      flexGrow={1}
      height='100%'
      minWidth={620}
      flexShrink={0}
      overflow='auto'
      maxWidth='calc(100vw - 840px)'
      css={scrollableContainerCss4px}
    >
      <RightTabs
        aiEnabled={aiEnabled}
        isReadOnlyMode={isReadOnlyMode}
        rightActiveTab={rightActiveTab}
        setRightActiveTab={setRightActiveTab}
      />
      {
        G.isNilOrEmpty(Component) ? null : <Component {...props} aiEnabled={aiEnabled} />
      }
    </Box>
  );
};

const ExpenseForm = (props) => {
  const { values, handleSubmit, isReadOnlyMode } = props;

  const { aiRecognizeRequest, aiRecognizeLoading } = useAiRecognize({ type: 'EXPENSE' });

  const [rightActiveTab, setRightActiveTab] = useState(0);

  const isEditMode = G.isNotNilAndNotEmpty(G.getGuidFromObject(values));

  const RightSectionComponent = G.isTrue(isEditMode) ? UpdateRightSection : RightSection;

  const submitBtnStyles = G.ifElse(isReadOnlyMode, { display: 'none' }, {});

  return (
    <form onSubmit={handleSubmit}>
      <Flex
        overflow='auto'
        alignItems='start'
        height='calc(100vh - 60px)'
        css={scrollableContainerCss4px}
      >
        <LeftSection
          {...props}
          aiRecognizeLoading={aiRecognizeLoading}
        />
        <RightSectionComponent
          {...props}
          rightActiveTab={rightActiveTab}
          setRightActiveTab={setRightActiveTab}
          aiRecognizeRequest={aiRecognizeRequest}
        />
      </Flex>
      <FormFooter
        submitBtnStyles={submitBtnStyles}
        submitDisabled={aiRecognizeLoading}
        boxStyles={{
          p: 15,
          borderTop: '1px solid',
          bg: G.getTheme('colors.whiteGrey'),
          borderColor: G.getTheme('colors.lightGrey'),
        }}
      />
    </form>
  );
};

export default enhance(ExpenseForm);
